// src/IssuesPage.js - System errors and issues tracking
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField as MuiTextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import AppleIcon from '@mui/icons-material/Apple';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Tabs, Tab } from '@mui/material';
import { supabase } from './supabaseClient';
import AppEventsPanel from './AppEventsPanel';

import { prettyStatus } from './labels';
const severityColors = {
  info: 'default',
  warning: 'warning',
  error: 'error',
  critical: 'error',
};

export default function IssuesPage() {
  // Two different questions: what someone decided to track, and what the app
  // and server actually reported. They were never in the same place before.
  const [tab, setTab] = useState('tracked');
  const [issues, setIssues] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('unresolved');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(null);
  const [appleCrashDialogOpen, setAppleCrashDialogOpen] = useState(false);
  const [detailIssue, setDetailIssue] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState(null);
  const [newIssue, setNewIssue] = useState({
    error_type: 'warning',
    severity: 'warning',
    title: '',
    message: '',
    source: 'manual',
  });
  const [appleCrashData, setAppleCrashData] = useState({
    crashReport: '',
    affectedUsers: '',
    crashDate: '',
  });
  const [resolutionNotes, setResolutionNotes] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('system_errors')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      setIssues(data || []);
      setAllIssues(data || []);
    } catch (err) {
      console.error('Error fetching issues:', err);
      setError(`Error: ${err.message || 'Failed to fetch issues'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter issues
  useEffect(() => {
    let filtered = [...allIssues];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(issue =>
        issue.title?.toLowerCase().includes(term) ||
        issue.message?.toLowerCase().includes(term) ||
        issue.source?.toLowerCase().includes(term) ||
        issue.error_type?.toLowerCase().includes(term)
      );
    }

    // Apply severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(issue => issue.severity === severityFilter);
    }

    // Apply resolved filter
    if (resolvedFilter === 'unresolved') {
      filtered = filtered.filter(issue => !issue.resolved);
    } else if (resolvedFilter === 'resolved') {
      filtered = filtered.filter(issue => issue.resolved);
    }

    setIssues(filtered);
  }, [searchTerm, severityFilter, resolvedFilter, allIssues]);

  const handleCreateIssue = async () => {
    if (!newIssue.title || !newIssue.message) {
      alert('Please fill in title and message');
      return;
    }

    setLoading(true);
    try {
      const { error: insertError } = await supabase
        .from('system_errors')
        .insert([{
          ...newIssue,
          details: {},
        }]);

      if (insertError) throw insertError;

      setCreateDialogOpen(false);
      setNewIssue({ error_type: 'warning', severity: 'warning', title: '', message: '', source: 'manual' });
      await fetchData();
    } catch (err) {
      console.error('Error creating issue:', err);
      alert(`Error: ${err.message || 'Failed to create issue'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportAppleCrash = async () => {
    if (!appleCrashData.crashReport) {
      alert('Please paste the crash report');
      return;
    }

    setLoading(true);
    try {
      // Parse crash report to extract key information
      const crashReport = appleCrashData.crashReport;
      const lines = crashReport.split('\n');
      
      // Try to extract crash reason
      let crashReason = 'Unknown';
      let crashLocation = '';
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes('Exception Type:') || lines[i].includes('Crashed Thread:')) {
          crashReason = lines[i].substring(lines[i].indexOf(':') + 1).trim();
        }
        if (lines[i].includes('Application Specific Information:') && i + 1 < lines.length) {
          crashReason = lines[i + 1].trim();
        }
        if (lines[i].includes('0x') && lines[i].includes('Living Life')) {
          crashLocation = lines[i].trim();
          break;
        }
      }

      const title = `Apple Crash Report${appleCrashData.affectedUsers ? ` - ${appleCrashData.affectedUsers} users` : ''}`;
      const message = crashReason || 'Crash reported from App Store Connect';
      
      const { error: insertError } = await supabase
        .from('system_errors')
        .insert([{
          error_type: 'crash',
          severity: 'critical',
          title: title,
          message: message,
          source: 'apple_app_store',
          details: {
            crashReport: crashReport,
            affectedUsers: appleCrashData.affectedUsers || 'Unknown',
            crashDate: appleCrashData.crashDate || new Date().toISOString(),
            crashLocation: crashLocation,
            fullReport: crashReport,
          },
        }]);

      if (insertError) throw insertError;

      setAppleCrashDialogOpen(false);
      setAppleCrashData({ crashReport: '', affectedUsers: '', crashDate: '' });
      await fetchData();
      alert('Apple crash report imported successfully');
    } catch (err) {
      console.error('Error importing Apple crash:', err);
      alert(`Error: ${err.message || 'Failed to import crash report'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveIssue = async (issueId) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error: updateError } = await supabase
        .from('system_errors')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id || null,
          resolution_notes: resolutionNotes || null,
        })
        .eq('id', issueId);

      if (updateError) throw updateError;

      setResolveDialogOpen(null);
      setResolutionNotes('');
      await fetchData();
    } catch (err) {
      console.error('Error resolving issue:', err);
      alert(`Error: ${err.message || 'Failed to resolve issue'}`);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSeverityFilter('all');
    setResolvedFilter('unresolved');
  };

  const hasActiveFilters = searchTerm || severityFilter !== 'all' || resolvedFilter !== 'unresolved';
  const unresolvedCount = allIssues.filter(i => !i.resolved).length;
  const criticalCount = allIssues.filter(i => !i.resolved && i.severity === 'critical').length;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4">
            System Issues & Errors
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {unresolvedCount} unresolved • {criticalCount} critical
          </Typography>
        </Box>
        {tab === 'tracked' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<AppleIcon />}
              variant="outlined"
              onClick={() => setAppleCrashDialogOpen(true)}
            >
              Import Apple Crash
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Issue
            </Button>
          </Box>
        )}
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="tracked" label="Tracked Issues" />
        <Tab value="events" label="App & Server Events" />
      </Tabs>

      {tab === 'events' && <AppEventsPanel />}

      {tab === 'tracked' && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tab === 'tracked' && <>
      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <MuiTextField
            placeholder="Search issues..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severityFilter}
              label="Severity"
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <MenuItem value="all">All Severities</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={resolvedFilter}
              label="Status"
              onChange={(e) => setResolvedFilter(e.target.value)}
            >
              <MenuItem value="unresolved">Unresolved</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : issues.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No issues found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue) => (
                <TableRow
                  key={issue.id}
                  hover
                  onClick={() => setDetailIssue(issue)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: issue.resolved ? 'action.disabledBackground' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={prettyStatus(issue.severity)}
                        size="small"
                        color={severityColors[issue.severity] || 'default'}
                      />
                      {issue.source === 'apple_app_store' && (
                        <AppleIcon fontSize="small" color="action" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={issue.resolved ? 'normal' : 'medium'}>
                      {issue.title}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {issue.message}
                  </TableCell>
                  <TableCell>{issue.source || '-'}</TableCell>
                  <TableCell>
                    {issue.created_at
                      ? new Date(issue.created_at).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {issue.resolved ? (
                      <Chip label="Resolved" size="small" color="success" />
                    ) : issue.escalated_at ? (
                      // Three states, not two. "Open" alone could not tell
                      // apart "nobody has looked at this" from "a report has
                      // gone out", which is the difference that decides whether
                      // to act again.
                      <Chip label="Report sent" size="small" color="info" />
                    ) : (
                      <Chip label="Open" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    {!issue.resolved && (
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        color="success"
                        onClick={(e) => { e.stopPropagation(); setResolveDialogOpen(issue.id); }}
                      >
                        Resolve
                      </Button>
                    )}
                    {issue.resolved && issue.resolution_notes && (
                      <Typography variant="caption" color="textSecondary">
                        {issue.resolution_notes}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Issue detail. Opened by clicking the row: the table shows a truncated
          message and nothing else, and the useful part of an issue — how many
          people it hit, which build, when it was last seen — lives in the
          details JSON that had no way of being read. */}
      <Dialog open={!!detailIssue} onClose={() => { setDetailIssue(null); setEscalateError(null); }} maxWidth="md" fullWidth>
        {detailIssue && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={prettyStatus(detailIssue.severity)}
                  size="small"
                  color={severityColors[detailIssue.severity] || 'default'}
                />
                <span>{detailIssue.title}</span>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {detailIssue.message}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 3, rowGap: 1, mb: 2 }}>
                <Typography variant="caption" color="textSecondary">Detected by</Typography>
                <Typography variant="body2">{detailIssue.source || '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                <Typography variant="body2">{detailIssue.error_type || '-'}</Typography>
                <Typography variant="caption" color="textSecondary">First recorded</Typography>
                <Typography variant="body2">
                  {detailIssue.created_at ? new Date(detailIssue.created_at).toLocaleString() : '-'}
                </Typography>
                {detailIssue.escalated_at && (
                  <>
                    <Typography variant="caption" color="textSecondary">Sent to engineering</Typography>
                    <Typography variant="body2">
                      {new Date(detailIssue.escalated_at).toLocaleString()}
                      {detailIssue.escalated_by ? ` by ${detailIssue.escalated_by}` : ''}
                    </Typography>
                  </>
                )}
              </Box>

              {detailIssue.details && Object.keys(detailIssue.details).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Diagnostics</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0, p: 2, borderRadius: 2, bgcolor: 'grey.100',
                      fontSize: '0.75rem', overflow: 'auto', maxHeight: 260,
                    }}
                  >
                    {JSON.stringify(detailIssue.details, null, 2)}
                  </Box>
                </>
              )}

              {detailIssue.escalated_at && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  A report was emailed
                  {detailIssue.escalated_by ? ` to ${detailIssue.escalated_by}` : ''} on{' '}
                  {new Date(detailIssue.escalated_at).toLocaleString()}. This issue stays open
                  until someone marks it resolved.
                </Typography>
              )}

              {escalateError && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {escalateError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setDetailIssue(null); setEscalateError(null); }}>Close</Button>
              {/* Needs nothing at all — no credentials, no network, no service.
                  Puts the whole report on the clipboard to paste wherever the
                  work actually happens. */}
              <Button
                onClick={async () => {
                  const d = detailIssue.details || {};
                  const lines = [
                    detailIssue.title,
                    '',
                    detailIssue.message,
                    '',
                    `Severity: ${prettyStatus(detailIssue.severity)}`,
                    `Type: ${detailIssue.error_type || '-'}`,
                    `Detected by: ${detailIssue.source || '-'}`,
                    `First recorded: ${detailIssue.created_at}`,
                    Object.keys(d).length ? `\nDiagnostics:\n${JSON.stringify(d, null, 2)}` : '',
                    `\nConsole issue id: ${detailIssue.id}`,
                  ].filter(Boolean).join('\n');
                  try {
                    await navigator.clipboard.writeText(lines);
                    setEscalateError('Copied to the clipboard.');
                  } catch {
                    setEscalateError('Could not reach the clipboard — select the diagnostics above and copy manually.');
                  }
                }}
              >
                Copy report
              </Button>
              {!detailIssue.resolved && !detailIssue.escalated_at && (
                <Button
                  variant="outlined"
                  disabled={escalating}
                  onClick={async () => {
                    setEscalating(true);
                    setEscalateError(null);
                    try {
                      const { data: sess } = await supabase.auth.getSession();
                      const res = await fetch('/api/issue-escalate', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${sess?.session?.access_token}`,
                        },
                        body: JSON.stringify({ issueId: detailIssue.id }),
                      });
                      const body = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error([body.error, body.hint].filter(Boolean).join(' ') || `Server returned ${res.status}`);
                      setDetailIssue({ ...detailIssue, escalated_at: new Date().toISOString(), escalated_by: body.sentTo });
                      fetchData();
                    } catch (err) {
                      setEscalateError(`Could not send it: ${err.message}`);
                    } finally {
                      setEscalating(false);
                    }
                  }}
                >
                  {escalating ? 'Sending…' : 'Email me this report'}
                </Button>
              )}
              {!detailIssue.resolved && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => { setResolveDialogOpen(detailIssue.id); setDetailIssue(null); }}
                >
                  Mark resolved
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Issue Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={newIssue.severity}
                label="Severity"
                onChange={(e) => setNewIssue({ ...newIssue, severity: e.target.value })}
              >
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Title"
              value={newIssue.title}
              onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Message"
              value={newIssue.message}
              onChange={(e) => setNewIssue({ ...newIssue, message: e.target.value })}
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="Source (optional)"
              value={newIssue.source}
              onChange={(e) => setNewIssue({ ...newIssue, source: e.target.value })}
              placeholder="e.g., dashboard, bookings, payments"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateIssue} variant="contained" disabled={loading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Issue Dialog */}
      <Dialog open={Boolean(resolveDialogOpen)} onClose={() => setResolveDialogOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Issue</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Mark this issue as resolved. You can add notes about how it was fixed.
          </DialogContentText>
          <TextField
            fullWidth
            label="Resolution Notes (optional)"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Describe how this issue was resolved..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(null)}>Cancel</Button>
          <Button
            onClick={() => handleResolveIssue(resolveDialogOpen)}
            variant="contained"
            color="success"
            disabled={loading}
          >
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Apple Crash Dialog */}
      <Dialog open={appleCrashDialogOpen} onClose={() => setAppleCrashDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppleIcon />
            Import Apple Crash Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Paste crash reports from App Store Connect. You can find these in:
            <br />
            <strong>App Store Connect → Your App → Analytics → Crashes</strong>
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Affected Users (optional)"
              value={appleCrashData.affectedUsers}
              onChange={(e) => setAppleCrashData({ ...appleCrashData, affectedUsers: e.target.value })}
              placeholder="e.g., 15 users"
              size="small"
            />
            <TextField
              fullWidth
              label="Crash Date (optional)"
              type="date"
              value={appleCrashData.crashDate}
              onChange={(e) => setAppleCrashData({ ...appleCrashData, crashDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              fullWidth
              label="Crash Report"
              value={appleCrashData.crashReport}
              onChange={(e) => setAppleCrashData({ ...appleCrashData, crashReport: e.target.value })}
              multiline
              rows={12}
              required
              placeholder="Paste the full crash report here..."
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
            <Alert severity="info">
              <Typography variant="body2">
                <strong>How to get crash reports:</strong>
                <br />
                1. Go to App Store Connect
                <br />
                2. Select your app → Analytics → Crashes
                <br />
                3. Click on a crash to view details
                <br />
                4. Copy the crash report and paste it here
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppleCrashDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleImportAppleCrash}
            variant="contained"
            disabled={!appleCrashData.crashReport || loading}
            startIcon={<CloudDownloadIcon />}
          >
            Import Crash Report
          </Button>
        </DialogActions>
      </Dialog>
      </>}
    </Box>
  );
}
